import type {AppBarProps} from '@mui/material/AppBar';
import AppBar from '@mui/material/AppBar';
import type {ContainerProps} from '@mui/material/Container';
import Container from '@mui/material/Container';
import type {Breakpoint, CSSObject, SxProps, Theme} from '@mui/material/styles';
import {styled} from '@mui/material/styles';
import {useScrollOffsetTop} from 'minimal-shared/hooks';
import {mergeClasses, varAlpha} from 'minimal-shared/utils';
import {ComponentProps, ReactNode} from 'react';

import {layoutClasses} from './classes';

// ----------------------------------------------------------------------

export type HeaderSectionProps = AppBarProps & {
  layoutQuery?: Breakpoint;
  disableOffset?: boolean;
  slots?: {
    leftArea?: ReactNode;
    rightArea?: ReactNode;
    topArea?: ReactNode;
    centerArea?: ReactNode;
    bottomArea?: ReactNode;
  };
  slotProps?: {
    container?: ContainerProps;
    centerArea?: ComponentProps<'div'> & {sx?: SxProps<Theme>};
  };
};

export function HeaderSection({
  sx,
  slots,
  slotProps,
  className,
  disableOffset,
  layoutQuery = 'md',
  ...other
}: HeaderSectionProps) {
  const {offsetTop: isOffset} = useScrollOffsetTop();

  return (
    <HeaderRoot
      position="sticky"
      color="transparent"
      isOffset={isOffset}
      disableOffset={disableOffset}
      className={mergeClasses([layoutClasses.header, className])}
      sx={[
        (theme) => ({
          ...(isOffset && {
            '--color': `var(--offset-color, ${theme.vars.palette.text.primary})`,
          }),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {slots?.topArea}

      <HeaderContainer layoutQuery={layoutQuery} {...slotProps?.container}>
        {slots?.leftArea}

        <HeaderCenterArea {...slotProps?.centerArea}>{slots?.centerArea}</HeaderCenterArea>

        {slots?.rightArea}
      </HeaderContainer>

      {slots?.bottomArea}
    </HeaderRoot>
  );
}

// ----------------------------------------------------------------------

type HeaderRootProps = Pick<HeaderSectionProps, 'disableOffset'> & {
  isOffset: boolean;
};

const HeaderRoot = styled(AppBar, {
  shouldForwardProp: (prop: string) => !['isOffset', 'disableOffset', 'sx'].includes(prop),
})<HeaderRootProps>(({isOffset, disableOffset, theme}) => {
  const pauseZindex = {top: -1, bottom: -2};

  const pauseStyles: CSSObject = {
    opacity: 0,
    content: '""',
    visibility: 'hidden',
    position: 'absolute',
    transition: theme.transitions.create(['opacity', 'visibility'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shorter,
    }),
  };

  const bgStyles: CSSObject = {
    ...theme.mixins.bgBlur({
      color: varAlpha(theme.vars.palette.background.defaultChannel, 0.8),
    }),
    ...pauseStyles,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: pauseZindex.top,
    ...(isOffset && {opacity: 1, visibility: 'visible'}),
  };

  return {
    zIndex: 'var(--layout-header-zIndex)',
    ...(!disableOffset && {'&::before': bgStyles}),
  };
});

const HeaderContainer = styled(Container, {
  shouldForwardProp: (prop: string) => !['layoutQuery', 'sx'].includes(prop),
})<Pick<HeaderSectionProps, 'layoutQuery'>>(({layoutQuery = 'md', theme}) => ({
  display: 'flex',
  alignItems: 'center',
  color: 'var(--color)',
  height: 'var(--layout-header-mobile-height)',
  [theme.breakpoints.up(layoutQuery)]: {height: 'var(--layout-header-desktop-height)'},
}));

const HeaderCenterArea = styled('div')(() => ({
  display: 'flex',
  flex: '1 1 auto',
  justifyContent: 'center',
}));
